/**
 * Tournament Synopsis Report — Full Enhanced Version
 * Sections:
 *  1. Sport Champions (Winners + Runners-up + Semis per sport)
 *  2. All-Round Excellence (players winning in multiple sports)
 *  3. Top Player Rankings
 *  4. Team Performers (max & min)
 *  5. Dominant Wins
 *  6. Nail-biters
 *  7. Bid vs Performance
 */
import { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Trophy, Download, RefreshCw, Printer } from 'lucide-react';
import SportIcon, { SPORT_LABELS } from '@/components/SportIcon';
import useAuthStore from '@/stores/authStore';
import { downloadCSV } from '@/lib/exportUtils';
import { toast } from 'sonner';

const token = () => localStorage.getItem('btccp_access_token');

function WinBar({ pct, color = '#22c55e' }) {
  return (
    <div className="flex-1 bg-gray-200 rounded-full overflow-hidden h-1.5">
      <div className="h-full rounded-full transition-all duration-700" style={{ width:`${pct}%`, backgroundColor:color }}/>
    </div>
  );
}

/** Reusable SVG Donut Pie Chart */
function PieChart({ data, size = 180, label, sublabel }) {
  const total = data.reduce((s, d) => s + d.value, 0) || 1;
  const cx = size / 2, cy = size / 2, r = size * 0.38;
  let cumAngle = -90;
  const toRad = (deg) => deg * Math.PI / 180;
  const slices = data.filter(d => d.value > 0).map(d => {
    const pct = d.value / total;
    const angle = pct * 360;
    const gap = angle > 2 ? 1.5 : 0;
    const s = cumAngle + gap / 2;
    cumAngle += angle;
    const e = cumAngle - gap / 2;
    const x1 = cx + r * Math.cos(toRad(s)), y1 = cy + r * Math.sin(toRad(s));
    const x2 = cx + r * Math.cos(toRad(e)), y2 = cy + r * Math.sin(toRad(e));
    const lg = angle > 180 ? 1 : 0;
    const path = angle > 359.5
      ? `M ${cx},${cy - r} A ${r},${r} 0 1,1 ${cx - 0.01},${cy - r} Z`
      : `M ${cx},${cy} L ${x1},${y1} A ${r},${r} 0 ${lg},1 ${x2},${y2} Z`;
    return { ...d, path, pct: Math.round(pct * 100) };
  });
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      {slices.map((s) => (
        <path key={`${s.name}-${s.pct}`} d={s.path} fill={s.color} stroke="white" strokeWidth="1.5" opacity="0.9">
          <title>{s.name}: {s.value} ({s.pct}%)</title>
        </path>
      ))}
      <circle cx={cx} cy={cy} r={r * 0.52} fill="white"/>
      {label    && <text x={cx} y={cy - 5}       textAnchor="middle" fontSize={size*0.08}  fontWeight="bold" fill="#374151">{label}</text>}
      {sublabel && <text x={cx} y={cy + size*0.07} textAnchor="middle" fontSize={size*0.055} fill="#9ca3af">{sublabel}</text>}
    </svg>
  );
}



function Section({ title, icon, subtitle, children, newPage = false }) {
  return (
    <div className="mb-8" style={newPage ? { breakBefore: 'page', pageBreakBefore: 'always' } : {}}>
      <div className="flex items-center gap-2 mb-1">
        <span className="text-xl">{icon}</span>
        <h2 className="text-base font-black text-gray-800 uppercase tracking-widest">{title}</h2>
        <div className="flex-1 h-px bg-gray-200"/>
      </div>
      {subtitle && <p className="text-xs text-gray-500 mb-3 ml-8">{subtitle}</p>}
      <div className="mt-3">{children}</div>
    </div>
  );
}

function StatPill({ label, value, color }) {
  return (
    <div className="flex flex-col items-center bg-gray-50 rounded-xl px-4 py-2.5 border border-gray-200">
      <p className="text-lg font-black tabular-nums" style={{ color }}>{value}</p>
      <p className="text-[10px] text-gray-500 uppercase tracking-wide">{label}</p>
    </div>
  );
}

export default function TournamentReportPage() {
  const { tid } = useParams();
  const navigate = useNavigate();
  const { activeOrgSlug, user } = useAuthStore();
  const orgSlug = user?.memberships?.[0]?.slug || activeOrgSlug || 'btcc';

  const [loading, setLoading]       = useState(true);
  const [tournament, setTournament] = useState(null);
  const [rankings, setRankings]     = useState([]);
  const [sports, setSports]         = useState([]);
  const [encounters, setEncounters] = useState([]);
  const [bidData, setBidData]       = useState({});
  const [sponsors, setSponsors]     = useState([]);
  const [teams, setTeams]           = useState([]);
  const [playerProfiles, setPlayerProfiles] = useState({}); // player_id → {dob, sports}

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const hdr = { Authorization: `Bearer ${token()}` };
      const results = await Promise.allSettled([
        fetch(`/api/orgs/${orgSlug}/tournaments/${tid}`, { headers: hdr }),
        fetch(`/api/orgs/${orgSlug}/rankings`, { headers: hdr }),
        fetch(`/api/orgs/${orgSlug}/scorer/encounters`, { headers: hdr }),
        fetch(`/api/orgs/${orgSlug}/tournaments/${tid}/auction/picks`, { headers: hdr }),
        fetch(`/api/orgs/${orgSlug}/sponsors?tournament_id=${tid}`, { headers: hdr }),
        fetch(`/api/orgs/${orgSlug}/teams`, { headers: hdr }),
        fetch(`/api/orgs/${orgSlug}/players?limit=999`, { headers: hdr }),
        fetch(`/api/orgs/${orgSlug}/sponsors`, { headers: hdr }),
      ]);
      const safeJson = async (r) => {
        try { return (r?.status==='fulfilled' && r.value?.ok) ? await r.value.json() : null; }
        catch { return null; }
      };
      const [tData,rData,eData,aData,spData,tmData,plData,orgSpData] = await Promise.all(results.map(safeJson));

      if (tData) setTournament(tData);
      if (rData) { setRankings(rData.rankings||[]); setSports(rData.sports||[]); }
      if (eData) setEncounters(Array.isArray(eData) ? eData : []);
      if (aData) {
        const map = {};
        (Array.isArray(aData) ? aData : []).forEach(p => {
          const name = (p.player_name||'').trim().toLowerCase();
          const bid  = parseFloat(p.bid_amount||0);
          if (name && bid>0) map[name] = bid;
        });
        setBidData(map);
      }
      const allSponsors = [];
      const seenIds = new Set();
      for (const list of [spData, orgSpData]) {
        (Array.isArray(list)?list:[]).forEach(s => {
          if (s?.id && !seenIds.has(s.id)) { seenIds.add(s.id); allSponsors.push(s); }
        });
      }
      setSponsors(allSponsors);
      if (tmData) setTeams(Array.isArray(tmData) ? tmData : tmData.items||[]);
      if (plData) {
        const plist = Array.isArray(plData) ? plData : plData.items||[];
        const map = {};
        plist.forEach(p => {
          if (p.id) map[p.id] = { dob: p.dob, sports: p.sports||[], full_name: p.full_name };
          const nm = (p.full_name||'').trim().toLowerCase();
          if (nm) map[nm] = { dob: p.dob, sports: p.sports||[], full_name: p.full_name };
        });
        setPlayerProfiles(map);
      }
    } catch (_) {}
    setLoading(false);
  }, [orgSlug, tid]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  const titleSponsor = sponsors.find(s =>
    s.type_id === 'stype_title' || s.type_id === 'title' ||
    (s.type_id||'').toLowerCase().includes('title') ||
    s.code === 'title'
  );

  const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString('en-IN', {
    day: 'numeric', month: 'long', year: 'numeric', timeZone: 'Asia/Kolkata'
  }) : null;

  /* ═══════════════ ANALYTICS ═══════════════ */

  const koEncs = encounters.filter(e => ['sf','final','3rd'].includes(e.round_type));

  // ── 1. Sport results: champion, runner-up, semis ──────────────
  const sportResults = {}; // sc → { champion, runnerUp, semis: [] }
  koEncs.filter(e => e.round_type === 'final').forEach(e => {
    const sc = e.sport_code || e.sport_results?.[0]?.sport_code;
    if (!sc) return;
    // Try encounter-level winner first, then fall back to sport_results sport_winner
    let winner = e.winner;
    if (!winner) {
      const sr = (e.sport_results||[]).find(s => (s.sport_code === sc || !s.sport_code) && s.sport_winner);
      if (sr?.sport_winner) winner = sr.sport_winner;
    }
    if (!winner) return; // genuinely not completed
    if (!sportResults[sc]) sportResults[sc] = { champion: null, runnerUp: null, semis: [] };
    const winTeam  = (winner === 'home' ? e.home_team : e.away_team)?.name || '—';
    const loseTeam = (winner === 'home' ? e.away_team : e.home_team)?.name || '—';
    sportResults[sc].champion = winTeam;
    sportResults[sc].runnerUp = loseTeam;
  });
  koEncs.filter(e => e.round_type === 'sf').forEach(e => {
    const sc = e.sport_code || e.sport_results?.[0]?.sport_code;
    if (!sc) return;
    let winner = e.winner;
    if (!winner) {
      const sr = (e.sport_results||[]).find(s => (s.sport_code === sc || !s.sport_code) && s.sport_winner);
      if (sr?.sport_winner) winner = sr.sport_winner;
    }
    if (!winner) return;
    if (!sportResults[sc]) sportResults[sc] = { champion: null, runnerUp: null, semis: [] };
    const loseSide = winner === 'home' ? 'away' : 'home';
    const loser = (loseSide === 'home' ? e.home_team : e.away_team)?.name;
    if (loser && !sportResults[sc].semis.includes(loser)) sportResults[sc].semis.push(loser);
  });

  // ── 0. Overall tournament stats ───────────────────────────────
  const statsByType = (() => {
    const matchesPerSport = {}; // sc → count of scored matches
    let totalMatches = 0;
    let cleanSweeps = 0; // encounters where one team won ALL sports

    encounters.forEach(enc => {
      const sportResults = enc.sport_results || [];
      // Count individual matches
      sportResults.forEach(sr => {
        const sc = sr.sport_code; if (!sc) return;
        const scored = (sr.matches||[]).filter(m => m.winner).length;
        matchesPerSport[sc] = (matchesPerSport[sc] || 0) + scored;
        totalMatches += scored;
      });
      // Clean sweep: all sports in this encounter won by the SAME side
      const completedSports = sportResults.filter(sr => sr.sport_winner && sr.sport_winner !== 'draw');
      if (completedSports.length >= 2) {
        const sides = completedSports.map(sr => sr.sport_winner);
        const allSame = sides.every(s => s === sides[0]);
        if (allSame) cleanSweeps += 1;
      }
    });

    // Player count by number of sports they PLAYED (not just won)
    const playerSportCount = {};
    rankings.forEach(p => {
      const played = Object.entries(p.per_sport||{}).filter(([,ss]) => ss.played > 0).length;
      playerSportCount[played] = (playerSportCount[played] || 0) + 1;
    });

    return { totalMatches, matchesPerSport, cleanSweeps, playerSportCount };
  })();

  // ── Players with 0 wins ────────────────────────────────────────
  const zeroWinPlayers = rankings
    .filter(p => p.wins === 0 && p.played > 0)
    .sort((a, b) => b.played - a.played);

  // ── Age analytics ─────────────────────────────────────────────
  const calcAge = (dob) => {
    if (!dob) return null;
    try { return Math.floor((Date.now() - new Date(dob)) / (365.25*24*60*60*1000)); }
    catch { return null; }
  };

  const ageStats = (() => {
    const ages = rankings.map(p => {
      const prof = playerProfiles[p.player_id] || playerProfiles[p.player_name?.toLowerCase()] || {};
      return { ...p, age: calcAge(prof.dob), sportsList: prof.sports || [] };
    }).filter(p => p.age !== null && p.age > 0);

    const allAges = ages.map(p => p.age);
    const avgAge = allAges.length ? Math.round(allAges.reduce((s,a)=>s+a,0)/allAges.length) : null;
    const minAge = allAges.length ? Math.min(...allAges) : null;
    const maxAge = allAges.length ? Math.max(...allAges) : null;

    const avgPerSport = {};
    sports.forEach(sc => {
      const sc_ages = ages.filter(p =>
        p.sportsList.includes(sc) || ((p.per_sport||{})[sc]?.played > 0)
      ).map(p => p.age);
      if (sc_ages.length)
        avgPerSport[sc] = { avg: Math.round(sc_ages.reduce((s,a)=>s+a,0)/sc_ages.length), count: sc_ages.length };
    });

    const AGE_GROUPS = [
      { label: '60+',      min: 60, max: Infinity, color: 'bg-purple-400' },
      { label: '55 – 59',  min: 55, max: 59,       color: 'bg-blue-400' },
      { label: '50 – 54',  min: 50, max: 54,       color: 'bg-teal-400' },
      { label: '45 – 49',  min: 45, max: 49,       color: 'bg-green-400' },
      { label: '40 – 44',  min: 40, max: 44,       color: 'bg-amber-400' },
      { label: 'Under 40', min: 0,  max: 39,       color: 'bg-red-400' },
    ];
    const groups = AGE_GROUPS.map(g => ({
      ...g,
      players: ages.filter(p => p.age >= g.min && p.age <= g.max).sort((a,b) => b.age-a.age),
    }));
    return { ages, avgAge, minAge, maxAge, avgPerSport, groups };
  })();

  const multiSportPlayers = rankings
    .map(p => ({
      ...p,
      sportsWithWins: Object.entries(p.per_sport||{}).filter(([,ss]) => ss.wins > 0),
    }))
    .filter(p => p.sportsWithWins.length >= 2)
    .sort((a,b) => b.sportsWithWins.length - a.sportsWithWins.length || b.wins - a.wins)
    .slice(0, 10);

  // ── 2b. Players grouped by number of sports with wins ─────────
  const sportCountGroups = {3: [], 2: [], 1: [], 0: []};
  rankings.forEach(p => {
    const sportsWon = Object.entries(p.per_sport||{}).filter(([,ss]) => ss.wins > 0).length;
    const played    = Object.entries(p.per_sport||{}).filter(([,ss]) => ss.played > 0).length;
    const key = Math.min(sportsWon, 3); // cap at 3 (shows 3+ as well)
    sportCountGroups[key].push({ ...p, sportsWon, sportsPlayed: played });
  });
  // For 3 group: include anyone with ≥3 sports won
  sportCountGroups[3] = rankings
    .map(p => {
      const sw = Object.entries(p.per_sport||{}).filter(([,ss]) => ss.wins > 0);
      return { ...p, sportsWon: sw.length };
    })
    .filter(p => p.sportsWon >= 3)
    .sort((a,b) => b.sportsWon - a.sportsWon || b.wins - a.wins);

  // ── 3. Top players overall ────────────────────────────────────
  const topPlayers = [...rankings].sort((a,b) => b.wins - a.wins || b.win_pct - a.win_pct).slice(0, 10);

  // ── 4. Team performance — individual match wins per team ─────
  const teamPerfMap = {}; // teamId → {name, color, matchWins, matchPlayed, sportWins}
  encounters.forEach(enc => {
    for (const [side, teamId, team] of [
      ['home', enc.home_team_id, enc.home_team],
      ['away', enc.away_team_id, enc.away_team],
    ]) {
      if (!teamId || !team?.name) continue;
      if (!teamPerfMap[teamId]) teamPerfMap[teamId] = { id: teamId, name: team.name, color: team.color, matchWins: 0, matchPlayed: 0, sportWins: {} };
      // Count individual match wins (not encounter wins)
      (enc.sport_results||[]).forEach(sr => {
        const sc = sr.sport_code;
        (sr.matches||[]).forEach(m => {
          if (!m.winner) return;
          teamPerfMap[teamId].matchPlayed += 1;
          if (m.winner === side || m.winner === `bye_${side}`) teamPerfMap[teamId].matchWins += 1;
        });
        if (sr.sport_winner === side) {
          teamPerfMap[teamId].sportWins[sc] = (teamPerfMap[teamId].sportWins[sc]||0)+1;
        }
      });
    }
  });

  const teamsArr = Object.values(teamPerfMap);
  // Total individual match wins across ALL teams (for % share)
  const totalAllMatchWins = teamsArr.reduce((s,t) => s+t.matchWins, 0) || 1;
  // Merge with full teams list so all 8 teams appear
  const knownTeamIds = new Set(teamsArr.map(t => t.id));
  const sortedTeams = [
    ...teamsArr,
    ...teams.filter(t => !knownTeamIds.has(t.id))
             .map(t => ({ id: t.id, name: t.name, color: t.color, matchWins: 0, matchPlayed: 0, sportWins: {} })),
  ].sort((a,b) => {
    const ar = a.matchPlayed > 0 ? a.matchWins/a.matchPlayed : 0;
    const br = b.matchPlayed > 0 ? b.matchWins/b.matchPlayed : 0;
    return br - ar || b.matchWins - a.matchWins;
  });
  const topTeam    = sortedTeams[0];
  const bottomTeam = sortedTeams[sortedTeams.length - 1];

  // Build player-to-team mapping — only map each side's own players to their team
  const playerTeamMap = {};
  encounters.forEach(enc => {
    for (const [side, teamId, team] of [
      ['home', enc.home_team_id, enc.home_team],
      ['away', enc.away_team_id, enc.away_team],
    ]) {
      if (!teamId || !team?.name) continue;
      // Only use this side's player fields — NOT the opponent's
      const fields = side === 'home'
        ? ['home_player', 'home_player2']
        : ['away_player', 'away_player2'];
      (enc.sport_results||[]).forEach(sr => {
        for (const field of fields) {
          (sr.matches||[]).forEach(m => {
            const name = m[field];
            if (name && !playerTeamMap[name.toLowerCase()])
              playerTeamMap[name.toLowerCase()] = { teamId, teamName: team.name };
          });
        }
      });
    }
  });
  // Map rankings players to teams, then group
  const teamPlayerPerf = {};
  rankings.forEach(p => {
    const info = playerTeamMap[p.player_name?.toLowerCase()];
    if (!info) return;
    if (!teamPlayerPerf[info.teamId]) teamPlayerPerf[info.teamId] = { teamName: info.teamName, players: [] };
    teamPlayerPerf[info.teamId].players.push(p);
  });
  const teamsByMaxPlayers = Object.entries(teamPlayerPerf)
    .map(([tid, {teamName, players}]) => {
      const scored = players.filter(p => p.played > 0);
      const avgWinPct = scored.length ? Math.round(scored.reduce((s,p)=>s+p.win_pct,0)/scored.length) : 0;
      return { teamId: tid, teamName, players: players.sort((a,b)=>b.wins-a.wins), scored: scored.length, avgWinPct };
    })
    .sort((a,b) => b.avgWinPct - a.avgWinPct);
  const bestTeamPlayers  = teamsByMaxPlayers[0];
  const worstTeamPlayers = teamsByMaxPlayers[teamsByMaxPlayers.length - 1];

  // ── Team player winner/non-winner counts ─────────────────────
  // Build a lookup: teamName → official player_count from teams API
  const teamOfficialCount = {};
  teams.forEach(t => { if (t.name) teamOfficialCount[t.name] = t.player_count || 0; });

  const teamWinStats = Object.entries(teamPlayerPerf).map(([, {teamName, players}]) => {
    const winners    = players.filter(p => p.wins > 0);
    const nonWinners = players.filter(p => p.wins === 0 && p.played > 0);
    // Official total from team roster; fall back to matched players count
    const officialTotal = teamOfficialCount[teamName] || players.length;
    const notPlayed     = officialTotal - winners.length - nonWinners.length;
    return { teamName, total: officialTotal, ranked: players.length, winners: winners.length, nonWinners: nonWinners.length, notPlayed: Math.max(0, notPlayed) };
  }).sort((a, b) => b.winners - a.winners);

  // ── Pie chart: team sport dominance (total sport encounter wins) ──
  const teamSportWinTotals = Object.values(teamPerfMap).map(t => ({
    name:  t.name,
    color: t.color || '#3b82f6',
    total: Object.values(t.sportWins).reduce((s, v) => s + v, 0),
  })).filter(t => t.total > 0).sort((a, b) => b.total - a.total);
  const grandTotalSportWins = teamSportWinTotals.reduce((s, t) => s + t.total, 0) || 1;

  // ── Overall points table (total match wins × 2 per team) ──────
  const teamMatchPoints = {};
  encounters.forEach(enc => {
    (enc.sport_results||[]).forEach(sr => {
      for (const [side, tid, team] of [
        ['home', enc.home_team_id, enc.home_team],
        ['away', enc.away_team_id, enc.away_team],
      ]) {
        if (!tid || !team?.name) continue;
        if (!teamMatchPoints[tid]) teamMatchPoints[tid] = { name: team.name, color: team.color, pts: 0, wins: 0 };
        (sr.matches||[]).forEach(m => {
          if (!m.winner) return;
          if (m.winner === side || m.winner === `bye_${side}`) {
            teamMatchPoints[tid].pts  += 2;
            teamMatchPoints[tid].wins += 1;
          }
        });
      }
    });
  });
  const overallPtsTable = Object.values(teamMatchPoints).sort((a, b) => b.pts - a.pts);
  const maxPts   = overallPtsTable[0]?.pts || 1;
  const totalPts = overallPtsTable.reduce((s, t) => s + t.pts, 0) || 1;

  // ── 5. Dominant wins (diff ≥ 10) ─────────────────────────────
  const dominantMatches = [];
  const closeMatches    = [];
  // Only KO rounds (SF, Final, 3rd place) for dominant/close match highlights
  encounters.filter(e => ['sf','final','3rd'].includes(e.round_type)).forEach(enc => {
    const ht = enc.home_team?.name || enc.home_seed || 'Home';
    const at = enc.away_team?.name || enc.away_seed || 'Away';
    (enc.sport_results||[]).forEach(sr => {
      const sc = sr.sport_code;
      (sr.matches||[]).forEach(m => {
        if (!m.winner || m.winner === 'draw') return;
        const hs = m.home_score ?? 0; const as = m.away_score ?? 0;
        if (hs === 0 && as === 0) return;
        const diff = Math.abs(hs - as);
        const hp = m.home_player || ht; const ap = m.away_player || at;
        const winner = m.winner === 'home' ? hp : ap;
        const loser  = m.winner === 'home' ? ap : hp;
        const ws = m.winner === 'home' ? hs : as;
        const ls = m.winner === 'home' ? as : hs;
        const entry = { sc, winner, loser, ws, ls, diff, slot: m.slot, rt: enc.round_type };
        if (diff >= 10 && ls > 0) dominantMatches.push(entry);
        else if (diff <= 2 && ls > 0) closeMatches.push(entry);
      });
    });
  });
  dominantMatches.sort((a,b) => b.diff - a.diff);
  closeMatches.sort((a,b) => a.diff - b.diff);

  // ── 6. Bid vs Performance ─────────────────────────────────────
  const bidPerformance = rankings
    .map(p => {
      const bid = bidData[p.player_name?.toLowerCase()] || 0;
      const valuePer1k = bid > 0 ? Math.round((p.wins / bid) * 1000) : 0;
      const costPerWin = bid > 0 && p.wins > 0 ? Math.round(bid / p.wins) : null;
      return { ...p, bid, valuePer1k, costPerWin };
    })
    .filter(p => p.bid > 0)
    .sort((a,b) => b.valuePer1k - a.valuePer1k);
  const maxBid  = Math.max(...bidPerformance.map(p => p.bid),  1);
  const maxWins = Math.max(...bidPerformance.map(p => p.wins), 1);
  const minCostPerWin = Math.min(...bidPerformance.filter(p=>p.costPerWin).map(p=>p.costPerWin), Infinity);

  /* ═══════════════ CSV EXPORT ═══════════════ */
  const exportCSV = () => {
    const rows = [];
    // Champions
    rows.push(['== SPORT CHAMPIONS ==']);
    rows.push(['Sport','Champion','Runner-up','Semi-finalists']);
    Object.entries(sportResults).forEach(([sc, r]) =>
      rows.push([SPORT_LABELS[sc]||sc, r.champion||'TBD', r.runnerUp||'TBD', (r.semis||[]).join(' & ')]));
    rows.push([]);
    // Multi-sport
    rows.push(['== ALL-ROUND EXCELLENCE ==']);
    rows.push(['Player','Sports Won','Total Wins','Win%','Sports']);
    multiSportPlayers.forEach(p => rows.push([p.player_name, p.sportsWithWins.length, p.wins, `${p.win_pct}%`,
      p.sportsWithWins.map(([sc,ss])=>`${SPORT_LABELS[sc]||sc}:${ss.wins}W`).join('; ')]));
    rows.push([]);
    // Rankings
    rows.push(['== TOP PLAYERS ==']);
    rows.push(['Rank','Player','Played','Wins','Win%',...sports.map(s=>SPORT_LABELS[s]||s)]);
    topPlayers.forEach((p,i) => rows.push([i+1,p.player_name,p.played,p.wins,`${p.win_pct}%`,
      ...sports.map(s=>`${p.per_sport?.[s]?.wins||0}W/${p.per_sport?.[s]?.played||0}P`)]));
    rows.push([]);
    // Teams
    rows.push(['== TEAM PERFORMANCE ==']);
    rows.push(['Team','Encounter Wins','Win%','Sports Won']);
    sortedTeams.forEach(t => {
      const epct = t.enc > 0 ? Math.round(t.wins/t.enc*100) : 0;
      rows.push([t.name, t.wins, `${epct}%`, Object.keys(t.sportWins).map(sc=>`${SPORT_LABELS[sc]||sc}:${t.sportWins[sc]}`).join('; ')]);
    });
    rows.push([]);
    rows.push(['== DOMINANT WINS ==']);
    rows.push(['Sport','Winner','Loser','Score','Margin']);
    dominantMatches.slice(0,20).forEach(m=>rows.push([SPORT_LABELS[m.sc]||m.sc,m.winner,m.loser,`${m.ws}-${m.ls}`,m.diff]));
    rows.push([]);
    rows.push(['== NAIL-BITERS ==']);
    rows.push(['Sport','Winner','Loser','Score','Margin']);
    closeMatches.slice(0,20).forEach(m=>rows.push([SPORT_LABELS[m.sc]||m.sc,m.winner,m.loser,`${m.ws}-${m.ls}`,m.diff]));
    if (bidPerformance.length > 0) {
      rows.push([]);
      rows.push(['== BID vs PERFORMANCE ==']);
      rows.push(['Player','Bid','Wins','Win%','Value/1K','Cost per Win (₹)']);
      bidPerformance.forEach(p=>rows.push([p.player_name,p.bid,p.wins,`${p.win_pct}%`,p.valuePer1k,p.costPerWin||'N/A']));
    }
    downloadCSV(`report_${tournament?.name?.replace(/\s+/g,'_')||'tournament'}`, [], rows);
    toast.success('Report exported!');
  };

  /* ═══════════════ RENDER ═══════════════ */

  if (loading) return (
    <div className="min-h-screen bg-white flex items-center justify-center">
      <RefreshCw className="h-8 w-8 text-amber-600 animate-spin"/>
    </div>
  );

  return (
    <div className="min-h-screen bg-white text-gray-800">
      <style>{`
        @media print {
          .no-print { display: none !important; }

          /* === PAGE SETUP === */
          @page { size: A4 portrait; margin: 8mm 8mm; }
          body { margin: 0 !important; font-size: 8pt !important; line-height: 1.3 !important; }

          /* === PRESERVE COLORS === */
          * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; color-adjust: exact !important; }

          /* === COMPACT LAYOUT === */
          .max-w-3xl { max-width: 100% !important; }
          .px-4, .px-5, .px-6 { padding-left: 8px !important; padding-right: 8px !important; }
          .py-3, .py-4, .py-5, .py-6 { padding-top: 6px !important; padding-bottom: 6px !important; }
          .py-2\\.5 { padding-top: 4px !important; padding-bottom: 4px !important; }
          .mb-4, .mb-5, .mb-6, .mb-8 { margin-bottom: 8px !important; }
          .gap-3, .gap-4 { gap: 6px !important; }
          .gap-6 { gap: 8px !important; }
          .space-y-3 > *, .space-y-4 > *, .space-y-5 > * { margin-top: 6px !important; }

          /* === FONT SIZES === */
          .text-3xl, .text-4xl { font-size: 14pt !important; }
          .text-2xl { font-size: 12pt !important; }
          .text-xl  { font-size: 11pt !important; }
          .text-lg  { font-size: 10pt !important; }
          .text-sm  { font-size: 8pt !important; }
          .text-xs  { font-size: 7pt !important; }
          .text-\\[10px\\], .text-\\[11px\\] { font-size: 6pt !important; }

          /* === TWO-COLUMN LAYOUT for data-heavy sections === */
          .print-2col { columns: 2 !important; column-gap: 8px !important; }
          .print-2col > * { break-inside: avoid !important; }

          /* Apply 2-col to grid sections */
          .grid.grid-cols-1.sm\\:grid-cols-2 { display: grid !important; grid-template-columns: 1fr 1fr !important; }
          .grid.grid-cols-1.sm\\:grid-cols-3 { display: grid !important; grid-template-columns: 1fr 1fr 1fr !important; }

          /* === SMALLER CHARTS === */
          svg { max-width: 100% !important; }

          /* === PAGE BREAKS === */
          .rounded-2xl { break-inside: avoid; }

          /* Keep section headings glued to their content */
          h2, .text-base.font-black.text-gray-700,
          [class*="uppercase"][class*="tracking-widest"] {
            break-after: avoid !important;
          }

          /* Section wrapper: allow break before but not after heading */
          .mb-8 { break-inside: auto; }
          .mb-8 > div:first-child { break-after: avoid !important; }

          /* === REMOVE DECORATIVE STUFF === */
          .shadow-sm, [class*="shadow"] { box-shadow: none !important; }
          .backdrop-blur { backdrop-filter: none !important; }

          /* Background colors */
          .bg-white { background-color: #ffffff !important; }
          .bg-gray-50 { background-color: #f9fafb !important; }
          .bg-amber-50, .bg-\\[rgba\\(245,197,24 { background-color: #fffbeb !important; }
          .bg-green-50 { background-color: #f0fdf4 !important; }
          .bg-red-50   { background-color: #fef2f2 !important; }
          .bg-blue-50  { background-color: #eff6ff !important; }

          /* === HERO HEADER compact === */
          .h-3.w-full { height: 4px !important; }

          /* === PIE CHARTS: shrink to fit 2 per row === */
          svg[width="180"] { width: 120px !important; height: 120px !important; }
          svg[width="160"] { width: 110px !important; height: 110px !important; }
        }
      `}</style>

      {/* Header */}
      <div className="no-print sticky top-0 z-10 bg-white border-b border-gray-200 px-4 sm:px-6 h-14 flex items-center justify-between shadow-sm">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate(`/admin/tournaments/${tid}`)} className="text-gray-500 hover:text-gray-800">
            <ArrowLeft className="h-5 w-5"/>
          </button>
          <Trophy className="h-5 w-5 text-amber-500"/>
          <span className="font-bold text-gray-800 truncate max-w-[180px]">{tournament?.name || 'Report'}</span>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={load} className="p-2 text-gray-400 hover:text-gray-700"><RefreshCw className="h-4 w-4"/></button>
          <button onClick={() => window.print()}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold text-gray-600 bg-gray-100 border border-gray-200 hover:bg-gray-200 transition-colors">
            <Printer className="h-3.5 w-3.5"/>PDF
          </button>
          <button onClick={exportCSV}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold text-green-700 bg-green-50 border border-green-200 hover:bg-green-100 transition-colors">
            <Download className="h-3.5 w-3.5"/>CSV
          </button>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-6">

        {/* ── Tournament Hero Header ────────────────────────────── */}
        <div className="mb-8 rounded-2xl border border-gray-200 overflow-hidden shadow-sm">
          {/* Banner / gradient strip */}
          <div className="h-3 bg-gradient-to-r from-amber-400 via-amber-500 to-amber-400"/>

          <div className="p-5 sm:p-6">
            {/* Title sponsor + tournament name row */}
            <div className="flex items-start justify-between gap-4 mb-4">
              <div className="flex-1 min-w-0">
                {/* Title sponsor label */}
                {titleSponsor && (
                  <div className="flex items-center gap-2 mb-1.5">
                    <span className="text-[10px] font-bold text-amber-600 uppercase tracking-widest">Presented by</span>
                    {titleSponsor.logo_url?.startsWith('data:') ? (
                      <img src={titleSponsor.logo_url} alt={titleSponsor.name}
                        className="h-6 object-contain max-w-[80px]" style={{ mixBlendMode:'multiply' }}/>
                    ) : (
                      <span className="text-xs font-black text-amber-700">{titleSponsor.name}</span>
                    )}
                  </div>
                )}
                <h1 className="text-2xl sm:text-3xl font-black text-gray-900 leading-tight">{tournament?.name}</h1>

                {/* Dates + Location */}
                <div className="flex flex-wrap items-center gap-3 mt-2 text-sm text-gray-600">
                  {(tournament?.start_date || tournament?.end_date) && (
                    <span className="flex items-center gap-1.5">
                      <svg className="h-3.5 w-3.5 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"/>
                      </svg>
                      {tournament.start_date && fmtDate(tournament.start_date)}
                      {tournament.start_date && tournament.end_date && ' – '}
                      {tournament.end_date && fmtDate(tournament.end_date)}
                    </span>
                  )}
                  {tournament?.venue_name && (
                    <span className="flex items-center gap-1.5">
                      <svg className="h-3.5 w-3.5 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"/>
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"/>
                      </svg>
                      {tournament.venue_name}
                    </span>
                  )}
                </div>
              </div>

              {/* Title sponsor logo (large, if available) */}
              {titleSponsor?.logo_url?.startsWith('data:') && (
                <div className="flex-shrink-0">
                  <img src={titleSponsor.logo_url} alt={titleSponsor.name}
                    className="h-16 w-16 object-contain rounded-xl border border-gray-100 bg-white p-1"
                    style={{ mixBlendMode:'multiply' }}/>
                  <p className="text-[9px] text-center text-gray-400 mt-1">Title Sponsor</p>
                </div>
              )}
            </div>

            {/* Stats pills */}
            <div className="flex items-center gap-3 flex-wrap mb-4">
              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-amber-50 text-amber-700 text-xs font-bold border border-amber-200">
                🏆 {sports.length} sports
              </span>
              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-green-50 text-green-700 text-xs font-bold border border-green-200">
                👥 {rankings.length} players
              </span>
              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-blue-50 text-blue-700 text-xs font-bold border border-blue-200">
                🏅 {teams.length} teams
              </span>
              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-purple-50 text-purple-700 text-xs font-bold border border-purple-200">
                ✅ {Object.keys(sportResults).filter(sc=>sportResults[sc].champion).length} sport{Object.keys(sportResults).filter(sc=>sportResults[sc].champion).length!==1?'s':''} completed
              </span>
            </div>

            {/* Participating teams with logos */}
            {teams.length > 0 && (
              <div>
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-2">Participating Teams</p>
                <div className="flex flex-wrap gap-2">
                  {teams.map(t => (
                    <div key={t.id} className="flex items-center gap-1.5 px-2 py-1 rounded-lg bg-gray-50 border border-gray-200">
                      <div className="h-6 w-6 rounded-md flex-shrink-0 flex items-center justify-center font-bold text-[9px] text-white overflow-hidden"
                        style={{ backgroundColor: t.color || '#3b82f6' }}>
                        {t.logo_url?.startsWith('data:')
                          ? <img src={t.logo_url} className="h-full w-full object-cover" alt={t.name}/>
                          : t.short_name?.[0] || t.name?.[0]}
                      </div>
                      <span className="text-xs font-semibold text-gray-700 whitespace-nowrap">{t.short_name || t.name}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {/* Credit line */}
            <div className="mt-3 pt-3 border-t border-gray-100 flex items-center justify-between">
              <p className="text-[10px] text-gray-400 italic">Tournament Report</p>
              <p className="text-[10px] text-gray-500 font-semibold">
                App created by <span className="text-amber-600 font-black">Sandeep Maheshwari</span> · BTCCP
              </p>
            </div>
          </div>
        </div>

        {/* ══ 0. OVERALL STATS ════════════════════════════════════════ */}
        <Section title="Tournament at a Glance" icon="📈" subtitle="Key numbers from the entire tournament">

          {/* Big stats row */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
            <div className="bg-white rounded-2xl border border-gray-200 px-4 py-4 text-center">
              <p className="text-3xl font-black text-amber-600">{statsByType.totalMatches}</p>
              <p className="text-xs text-gray-500 mt-1 font-semibold uppercase tracking-wide">Total Matches</p>
            </div>
            <div className="bg-white rounded-2xl border border-gray-200 px-4 py-4 text-center">
              <p className="text-3xl font-black text-blue-600">{encounters.filter(e=>!['sf','final','3rd'].includes(e.round_type)).length}</p>
              <p className="text-xs text-gray-500 mt-1 font-semibold uppercase tracking-wide">League Ties</p>
            </div>
            <div className="bg-white rounded-2xl border border-gray-200 px-4 py-4 text-center">
              <p className="text-3xl font-black text-green-600">{statsByType.cleanSweeps}</p>
              <p className="text-xs text-gray-500 mt-1 font-semibold uppercase tracking-wide">Clean Sweeps</p>
              <p className="text-[10px] text-gray-400 mt-0.5">1 team won all sports</p>
            </div>
            <div className="bg-white rounded-2xl border border-gray-200 px-4 py-4 text-center">
              <p className="text-3xl font-black text-purple-600">{rankings.length}</p>
              <p className="text-xs text-gray-500 mt-1 font-semibold uppercase tracking-wide">Active Players</p>
            </div>
          </div>

          {/* Matches per sport */}
          <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden mb-3">
            <div className="px-4 py-2.5 bg-gray-50 border-b border-gray-100">
              <p className="text-xs font-bold text-gray-500 uppercase tracking-widest">Matches Played Per Sport</p>
            </div>
            <div className="divide-y divide-gray-50">
              {Object.entries(statsByType.matchesPerSport)
                .sort((a,b) => b[1]-a[1])
                .map(([sc, cnt]) => {
                  const maxM = Math.max(...Object.values(statsByType.matchesPerSport), 1);
                  const pct  = Math.round(cnt / maxM * 100);
                  return (
                    <div key={sc} className="flex items-center gap-3 px-4 py-2.5">
                      <SportIcon sport={sc} size={20}/>
                      <p className="text-xs font-semibold text-gray-700 w-28 flex-shrink-0">{SPORT_LABELS[sc]||sc}</p>
                      <div className="flex-1 bg-gray-100 rounded-full overflow-hidden h-2">
                        <div className="h-full rounded-full bg-amber-400 transition-all" style={{ width:`${pct}%` }}/>
                      </div>
                      <span className="text-xs font-black text-gray-700 w-8 text-right">{cnt}</span>
                    </div>
                  );
                })}
            </div>
          </div>

          {/* Player participation by sport count */}
          <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
            <div className="px-4 py-2.5 bg-gray-50 border-b border-gray-100">
              <p className="text-xs font-bold text-gray-500 uppercase tracking-widest">Players by Number of Sports Participated</p>
            </div>
            <div className="flex flex-wrap gap-2 px-4 py-3">
              {[5, 4, 3, 2, 1].map(n => {
                const cnt = statsByType.playerSportCount[n] || 0;
                if (cnt === 0) return null;
                const colors = {
                  5: 'bg-purple-50 text-purple-700 border-purple-200',
                  4: 'bg-amber-50 text-amber-700 border-amber-200',
                  3: 'bg-blue-50 text-blue-700 border-blue-200',
                  2: 'bg-green-50 text-green-700 border-green-200',
                  1: 'bg-gray-50 text-gray-600 border-gray-200',
                };
                return (
                  <div key={n} className={`flex items-center gap-2 px-3 py-2 rounded-xl border ${colors[n]}`}>
                    <span className="text-xl font-black">{cnt}</span>
                    <div>
                      <p className="text-xs font-bold">{n} sport{n>1?'s':''}</p>
                      <p className="text-[10px] opacity-70">{Math.round(cnt/rankings.length*100)}% of players</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </Section>


        {/* ══ 0b. AGE PROFILE ═════════════════════════════════════════ */}
        {ageStats.avgAge && (
          <Section title="Player Age Profile" icon="🎂"
            subtitle={`Ages of ${ageStats.ages.length} players with DOB on record · avg ${ageStats.avgAge} yrs · youngest ${ageStats.minAge} · oldest ${ageStats.maxAge}`}>

            {/* Overall average only — no per-sport breakdown */}
            <div className="flex items-center gap-4 mb-4">
              <div className="bg-white rounded-2xl border-2 border-amber-200 px-6 py-4 text-center">
                <p className="text-4xl font-black text-amber-600">{ageStats.avgAge}</p>
                <p className="text-xs text-gray-500 mt-1 font-semibold">Overall Avg Age</p>
                <p className="text-[10px] text-gray-400">{ageStats.ages.length} players · youngest {ageStats.minAge} · oldest {ageStats.maxAge}</p>
              </div>
            </div>

            {/* Two Pie Charts — no legend (full breakdown shown below) */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
              {/* Pie 1: Age distribution */}
              <div className="bg-white rounded-2xl border border-gray-200 p-4 flex flex-col items-center">
                <p className="text-xs font-bold text-gray-500 uppercase tracking-widest mb-3 text-center">Age Distribution</p>
                <PieChart
                  size={180}
                  label={ageStats.avgAge}
                  sublabel="avg age"
                  data={ageStats.groups.filter(g=>g.players.length>0).map(g => ({
                    name:  g.label,
                    value: g.players.length,
                    color: { 'bg-purple-400':'#a78bfa', 'bg-blue-400':'#60a5fa', 'bg-teal-400':'#2dd4bf',
                             'bg-green-400':'#4ade80', 'bg-amber-400':'#fbbf24', 'bg-red-400':'#f87171' }[g.color] || '#9ca3af',
                  }))}
                />
              </div>

              {/* Pie 2: Sport participation */}
              <div className="bg-white rounded-2xl border border-gray-200 p-4 flex flex-col items-center">
                <p className="text-xs font-bold text-gray-500 uppercase tracking-widest mb-3 text-center">Sports Participation</p>
                {(() => {
                  const SPORT_PIE_COLORS = { 5:'#8b5cf6', 4:'#f59e0b', 3:'#3b82f6', 2:'#22c55e', 1:'#9ca3af' };
                  const buckets = [5,4,3,2,1].map(n => ({
                    name: `${n} sport${n>1?'s':''}`,
                    value: statsByType.playerSportCount[n] || 0,
                    color: SPORT_PIE_COLORS[n],
                  })).filter(b => b.value > 0);
                  return <PieChart size={180} label={rankings.length} sublabel="players" data={buckets}/>;
                })()}
              </div>
            </div>

            {/* Full age group analysis */}
            <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
              <div className="px-4 py-2.5 bg-gray-50 border-b border-gray-100">
                <p className="text-xs font-bold text-gray-500 uppercase tracking-widest">Age Group Analysis</p>
              </div>
              <div className="divide-y divide-gray-50">
                {ageStats.groups.filter(g => g.players.length > 0).map(g => {
                  const pct  = Math.round(g.players.length / ageStats.ages.length * 100);
                  const avgG = Math.round(g.players.reduce((s,p)=>s+p.age,0)/g.players.length);
                  const hex  = { 'bg-purple-400':'#a78bfa','bg-blue-400':'#60a5fa','bg-teal-400':'#2dd4bf',
                                  'bg-green-400':'#4ade80','bg-amber-400':'#fbbf24','bg-red-400':'#f87171' }[g.color];
                  return (
                    <div key={g.label} className="flex items-center gap-3 px-4 py-2.5">
                      <div className="h-3 w-3 rounded-full flex-shrink-0" style={{ backgroundColor: hex }}/>
                      <p className="text-xs font-black text-gray-700 w-20 flex-shrink-0">{g.label}</p>
                      <div className="flex-1 bg-gray-100 rounded-full h-2 overflow-hidden">
                        <div className="h-full rounded-full" style={{ width:`${pct}%`, backgroundColor: hex }}/>
                      </div>
                      <span className="text-xs font-bold text-gray-800 w-8 text-right">{g.players.length}</span>
                      <span className="text-[10px] text-gray-400 w-10 text-right">{pct}%</span>
                      <span className="text-[10px] text-gray-500 font-semibold w-14 text-right">avg {avgG}y</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </Section>
        )}


        {/* ══ 1. SPORT CHAMPIONS ══════════════════════════════════════ */}
        <Section title="Sport Results" icon="🏆" subtitle="Winners, Runner-ups and Semi-finalists per sport" newPage>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {sports.map(sc => {
              const r = sportResults[sc] || {};
              return (
                <div key={sc} className="bg-white rounded-2xl border overflow-hidden"
                  style={{ borderColor: r.champion ? 'rgba(245,197,24,0.35)' : '#252a37' }}>
                  {/* Sport header */}
                  <div className="flex items-center gap-2 px-4 py-2.5 bg-gray-50 border-b border-gray-200">
                    <SportIcon sport={sc} size={22}/>
                    <span className="font-bold text-sm text-gray-800">{SPORT_LABELS[sc]||sc}</span>
                    {!r.champion && <span className="ml-auto text-[10px] text-gray-500 italic">In Progress</span>}
                  </div>
                  <div className="px-4 py-3 space-y-2">
                    {r.champion ? (
                      <>
                        <div className="flex items-center gap-2">
                          <span className="text-xl leading-none flex-shrink-0">🏆</span>
                          <div>
                            <p className="text-[10px] text-gray-500 uppercase tracking-widest">Champion</p>
                            <p className="text-sm font-black text-amber-600">{r.champion}</p>
                          </div>
                        </div>
                        {r.runnerUp && (
                          <div className="flex items-center gap-2">
                            <span className="text-xl leading-none flex-shrink-0">🥈</span>
                            <div>
                              <p className="text-[10px] text-gray-500 uppercase tracking-widest">Runner-up</p>
                              <p className="text-sm font-semibold text-gray-500">{r.runnerUp}</p>
                            </div>
                          </div>
                        )}
                        {r.semis?.length > 0 && (
                          <div className="flex items-start gap-2">
                            <span className="text-xl leading-none flex-shrink-0">🥉</span>
                            <div>
                              <p className="text-[10px] text-gray-500 uppercase tracking-widest">Semi-finalists</p>
                              <p className="text-xs text-gray-500">{r.semis.join(' · ')}</p>
                            </div>
                          </div>
                        )}
                      </>
                    ) : (
                      <p className="text-xs text-gray-500 py-1">Finals not yet played</p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </Section>

        {/* ══ 2. ALL-ROUND EXCELLENCE ════════════════════════════════ */}
        {multiSportPlayers.length > 0 && (
          <Section title="All-Round Excellence" icon="⭐" newPage
            subtitle="Players with wins across multiple sports — the true all-rounders">
            <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden">
              <div className="divide-y divide-gray-100">
                {multiSportPlayers.map((p, i) => (
                  <div key={p.player_id} className="flex items-center gap-3 px-4 py-3">
                    <div className="text-center w-7 flex-shrink-0">
                      <span className="text-sm font-black" style={{ color: i===0?'#f5c518':i===1?'#94a3b8':i===2?'#b45309':'#8a93a3' }}>
                        {i===0?'🌟':i===1?'⭐':i+1}
                      </span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-bold text-gray-800 truncate">{p.player_name}</p>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {p.sportsWithWins.map(([sc, ss]) => (
                          <span key={sc} className="text-[9px] px-1.5 py-0.5 rounded-full bg-green-50 text-green-600 border border-green-200 flex items-center gap-0.5">
                            <SportIcon sport={sc} size={14}/> {ss.wins}W
                          </span>
                        ))}
                      </div>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className="text-sm font-black text-green-600">{p.wins} wins</p>
                      <p className="text-[10px] text-amber-600">{p.sportsWithWins.length} sports</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </Section>
        )}

        {/* ══ 3. SPORT COVERAGE ══════════════════════════════════════ */}
        <Section title="Player Sport Coverage" icon="🎯" newPage
          subtitle="Players grouped by the number of sports they won matches in">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
            {[
              { label: '3+ Sports', emoji: '🌟', players: sportCountGroups[3], color: 'amber', bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-700', badge: 'bg-amber-100 text-amber-700 border-amber-200' },
              { label: '2 Sports',  emoji: '⭐', players: sportCountGroups[2].slice(0,15), color: 'blue',  bg: 'bg-blue-50',  border: 'border-blue-200',  text: 'text-blue-700',  badge: 'bg-blue-100 text-blue-700 border-blue-200' },
              { label: '1 Sport',   emoji: '🏅', players: sportCountGroups[1].slice(0,15), color: 'gray',  bg: 'bg-gray-50',  border: 'border-gray-200',  text: 'text-gray-600',  badge: 'bg-gray-100 text-gray-600 border-gray-200' },
            ].map(grp => (
              <div key={grp.label} className={`rounded-2xl border overflow-hidden ${grp.border}`}>
                {/* Header */}
                <div className={`px-4 py-2.5 ${grp.bg} border-b ${grp.border} flex items-center justify-between`}>
                  <div className="flex items-center gap-2">
                    <span className="text-lg">{grp.emoji}</span>
                    <p className={`text-xs font-black uppercase tracking-wider ${grp.text}`}>{grp.label}</p>
                  </div>
                  <span className={`text-xs font-black px-2 py-0.5 rounded-full border ${grp.badge}`}>
                    {grp.players.length} players
                  </span>
                </div>
                {/* Player list */}
                <div className="bg-white divide-y divide-gray-50">
                  {grp.players.map((p, i) => {
                    const bid = bidData[p.player_name?.toLowerCase()] || 0;
                    const cpw = bid > 0 && p.wins > 0 ? Math.round(bid / p.wins) : null;
                    return (
                    <div key={p.player_id} className="px-4 py-2 text-xs">
                      <div className="flex items-center gap-2">
                        <span className="text-gray-300 w-4 flex-shrink-0">{i+1}</span>
                        <p className="flex-1 font-semibold text-gray-800 truncate">{p.player_name}</p>
                        <div className="flex items-center gap-1 flex-shrink-0">
                          {Object.entries(p.per_sport||{}).filter(([,ss])=>ss.wins>0).map(([sc])=>(
                            <span key={sc} title={SPORT_LABELS[sc]||sc}><SportIcon sport={sc} size={14}/></span>
                          ))}
                        </div>
                        <span className="font-black text-green-600 flex-shrink-0">{p.wins}W</span>
                      </div>
                      {(bid > 0 || cpw) && (
                        <div className="flex items-center gap-3 mt-0.5 ml-6 text-[10px]">
                          {bid > 0 && (
                            <span className="text-blue-600 font-mono">
                              ₹{bid >= 1000 ? `${(bid/1000).toFixed(1)}K` : bid}
                            </span>
                          )}
                          {cpw && (
                            <span className="text-purple-600 font-semibold">
                              ₹{cpw >= 1000 ? `${(cpw/1000).toFixed(1)}K` : cpw}/win
                            </span>
                          )}
                          {bid > 0 && p.wins === 0 && (
                            <span className="text-red-500 font-semibold">0 wins</span>
                          )}
                        </div>
                      )}
                    </div>
                    );
                  })}
                  {grp.players.length === 0 && (
                    <p className="px-4 py-3 text-[10px] text-gray-400 text-center italic">None yet</p>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Summary bar */}
          <div className="bg-gray-50 border border-gray-200 rounded-2xl px-5 py-3 flex flex-wrap items-center gap-4 text-xs">
            <span className="text-gray-500 font-semibold">Win distribution:</span>
            {[3,2,1,0].map(n => {
              const cnt = n === 3 ? sportCountGroups[3].length : sportCountGroups[n].length;
              const pct = rankings.length > 0 ? Math.round(cnt/rankings.length*100) : 0;
              const labels = {3:'3+ sports',2:'2 sports',1:'1 sport',0:'0 wins'};
              const colors = {3:'text-amber-600',2:'text-blue-600',1:'text-gray-600',0:'text-red-400'};
              return (
                <span key={n} className={`font-bold ${colors[n]}`}>
                  {cnt} players — {labels[n]} ({pct}%)
                </span>
              );
            })}
          </div>
        </Section>

        {/* ══ 3-original. TOP PLAYERS ═══════════════════════════════════ */}
        {topPlayers.length > 0 && (
          <Section title="Player Rankings" icon="📊" subtitle="Ranked by total wins across all sports" newPage>
            <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-gray-200 bg-gray-50">
                    <th className="text-left px-3 py-2.5 text-gray-500 w-8">#</th>
                    <th className="text-left px-3 py-2.5 text-gray-500">Player</th>
                    {sports.slice(0,5).map(sc => (
                      <th key={sc} className="text-center px-1 py-2.5 text-gray-500" title={SPORT_LABELS[sc]||sc}>
                        <SportIcon sport={sc} size={20}/>
                      </th>
                    ))}
                    <th className="text-center px-2 py-2.5 text-amber-600">W</th>
                    <th className="text-center px-3 py-2.5 text-gray-500 w-20">Win%</th>
                  </tr>
                </thead>
                <tbody>
                  {topPlayers.map((p, i) => {
                    const pct = p.win_pct || 0;
                    const color = pct >= 70 ? '#22c55e' : pct >= 40 ? '#f5c518' : '#ef4444';
                    const medal = i===0?'🥇':i===1?'🥈':i===2?'🥉':null;
                    return (
                      <tr key={p.player_id} className={`border-b border-gray-200/40 last:border-0 ${i<3?'bg-amber-50/20':''}`}>
                        <td className="px-3 py-2.5 text-center">
                          {medal ? <span>{medal}</span> : <span className="text-gray-500">{i+1}</span>}
                        </td>
                        <td className="px-3 py-2.5 font-semibold text-gray-800 max-w-[120px] truncate">{p.player_name}</td>
                        {sports.slice(0,5).map(sc => {
                          const sw = p.per_sport?.[sc]?.wins || 0;
                          return (
                            <td key={sc} className="px-1 py-2.5 text-center tabular-nums" style={{ color: sw>0?'#22c55e':'#8a93a3' }}>
                              {sw > 0 ? sw : '—'}
                            </td>
                          );
                        })}
                        <td className="px-2 py-2.5 text-center font-black text-green-600">{p.wins}</td>
                        <td className="px-3 py-2.5">
                          <div className="flex items-center gap-1">
                            <WinBar pct={pct} color={color}/>
                            <span className="text-[10px] font-bold w-7 text-right" style={{ color }}>{pct}%</span>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Section>
        )}

        {sortedTeams.length > 0 && (
          <Section title="Team Performers" icon="⚡" newPage
            subtitle="All teams ranked by individual match win rate (wins ÷ matches played)">
            <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden">
              <div className="px-4 py-2.5 bg-gray-50 border-b border-gray-200 flex items-center justify-between text-[10px] text-gray-500 font-bold uppercase tracking-widest">
                <span>All Teams</span>
                <span className="text-[10px] font-normal text-gray-400 normal-case">
                  Win% = matches won ÷ matches played
                </span>
              </div>
              <div className="divide-y divide-gray-100">
                {sortedTeams.map((t, i) => {
                  const winRate = t.matchPlayed > 0 ? Math.round(t.matchWins / t.matchPlayed * 100) : 0;
                  const medal = i===0?'🥇':i===1?'🥈':i===2?'🥉':null;
                  return (
                    <div key={t.id} className="flex items-center gap-3 px-4 py-2.5">
                      <span className="text-sm w-7 text-center flex-shrink-0">{medal || <span className="text-[10px] text-gray-400">{i+1}</span>}</span>
                      <div className="h-6 w-6 rounded-md flex-shrink-0 flex items-center justify-center font-bold text-[9px] text-white"
                        style={{ backgroundColor: t.color||'#3b82f6' }}>
                        {t.name?.[0]}
                      </div>
                      <p className="text-xs font-semibold text-gray-800 flex-1 truncate">{t.name}</p>
                      <span className="text-[10px] text-gray-400 flex-shrink-0">{t.matchWins}W/{t.matchPlayed}P</span>
                      <div className="flex items-center gap-2 flex-shrink-0 w-28">
                        <div className="flex-1 bg-gray-100 rounded-full h-2 overflow-hidden">
                          <div className="h-full rounded-full transition-all"
                            style={{ width:`${winRate}%`, backgroundColor: winRate>=60?'#22c55e':winRate>=40?'#f59e0b':'#ef4444' }}/>
                        </div>
                        <span className="text-xs font-black w-9 text-right flex-shrink-0"
                          style={{ color: winRate>=60?'#16a34a':winRate>=40?'#b45309':'#dc2626' }}>
                          {t.matchPlayed > 0 ? `${winRate}%` : '—'}
                        </span>
                      </div>
                      <div className="flex flex-wrap gap-0.5 flex-shrink-0 max-w-[60px]">
                        {Object.keys(t.sportWins).map(sc => (
                          <span key={sc} className="text-[8px]"><SportIcon sport={sc} size={12}/></span>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="px-4 py-2 bg-gray-50 border-t border-gray-100 text-[10px] text-gray-400 text-center">
                Win% = matches won ÷ matches played × 100
              </div>
            </div>
          </Section>
        )}

        {/* ══ 4c. TEAM PLAYER WIN BREAKDOWN ══════════════════════════ */}
        {teamWinStats.length > 0 && (
          <Section title="Team Player Performance Breakdown" icon="👥" newPage
            subtitle="How many players from each team won, lost, or didn't play">
            <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden">
              <div className="px-4 py-2.5 bg-gray-50 border-b border-gray-100 grid grid-cols-4 gap-2 text-[10px] font-bold text-gray-500 uppercase tracking-widest">
                <span>Team</span>
                <span className="text-center text-green-600">Winners</span>
                <span className="text-center text-red-500">0-Win</span>
                <span className="text-center text-gray-400">Not Played</span>
              </div>
              <div className="divide-y divide-gray-50">
                {teamWinStats.map((t, i) => {
                  const winPct = t.total > 0 ? Math.round(t.winners / t.total * 100) : 0;
                  return (
                    <div key={t.teamName} className="px-4 py-3">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-[10px] text-gray-300 w-5">{i+1}</span>
                        <p className="text-xs font-bold text-gray-800 flex-1 truncate">{t.teamName}</p>
                        <span className="text-[10px] text-gray-400">{t.total} players</span>
                      </div>
                      {/* Stacked bar: winners (green) | 0-win played (red) | not in records (gray) */}
                      <div className="flex rounded-full overflow-hidden h-2.5 ml-5">
                        <div className="bg-green-400 h-full transition-all" style={{ width:`${t.total>0?Math.round(t.winners/t.total*100):0}%` }} title={`${t.winners} winners`}/>
                        <div className="bg-red-300 h-full transition-all" style={{ width:`${t.total>0?Math.round(t.nonWinners/t.total*100):0}%` }} title={`${t.nonWinners} 0-win`}/>
                        <div className="bg-gray-200 h-full flex-1" title={`${t.notPlayed} no matches/not tracked`}/>
                      </div>
                      <div className="flex items-center gap-3 mt-1.5 ml-5 text-[10px]">
                        <span className="text-green-600 font-bold">{t.winners} won</span>
                        <span className="text-red-500">{t.nonWinners} no-win</span>
                        {t.notPlayed > 0 && <span className="text-gray-400">{t.notPlayed} not tracked</span>}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </Section>
        )}

        {/* ══ 4d. PIE CHART — TEAM SPORT DOMINANCE ══════════════════ */}
        {teamSportWinTotals.length > 0 && (
          <Section title="Sport Win Dominance" icon="🥧" newPage
            subtitle="Each sport's match wins distributed by team">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {sports.map(sc => {
                // Per-sport team match wins
                const sportTeamWins = Object.values(teamPerfMap)
                  .map(t => ({
                    name:  t.name,
                    color: t.color || '#3b82f6',
                    value: (() => {
                      let wins = 0;
                      encounters.forEach(enc => {
                        for (const [side, tid2] of [['home',enc.home_team_id],['away',enc.away_team_id]]) {
                          if (tid2 !== t.id) continue;
                          (enc.sport_results||[]).filter(sr=>sr.sport_code===sc).forEach(sr => {
                            (sr.matches||[]).forEach(m => {
                              if (m.winner === side || m.winner === `bye_${side}`) wins++;
                            });
                          });
                        }
                      });
                      return wins;
                    })(),
                  }))
                  .filter(t => t.value > 0)
                  .sort((a,b) => b.value - a.value);
                const sportTotal = sportTeamWins.reduce((s,t)=>s+t.value,0)||1;
                if (sportTeamWins.length === 0) return null;
                return (
                  <div key={sc} className="bg-white rounded-2xl border border-gray-200 p-4">
                    <div className="flex items-center gap-2 mb-3">
                      <SportIcon sport={sc} size={22}/>
                      <p className="text-xs font-bold text-gray-700">{SPORT_LABELS[sc]||sc}</p>
                      <span className="ml-auto text-[10px] text-gray-400">{sportTotal} wins</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <PieChart size={110} label={sportTotal} sublabel="wins" data={sportTeamWins}/>
                      <div className="flex-1 space-y-1.5">
                        {sportTeamWins.map(t => {
                          const pct = Math.round(t.value/sportTotal*100);
                          return (
                            <div key={t.name} className="flex items-center gap-1.5">
                              <div className="h-2.5 w-2.5 rounded-sm flex-shrink-0" style={{backgroundColor:t.color}}/>
                              <p className="text-[10px] text-gray-700 flex-1 truncate">{t.name}</p>
                              <span className="text-[10px] font-black text-gray-800">{t.value}W</span>
                              <span className="text-[9px] text-gray-400 w-7 text-right">{pct}%</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                );
              }).filter(Boolean)}
            </div>
          </Section>
        )}

        {/* ══ 4e. OVERALL POINTS TABLE ═══════════════════════════════ */}
        {overallPtsTable.length > 0 && (
          <Section title="Overall Points Table" icon="📋" newPage
            subtitle="Total match points (2 pts per individual match win) across all sports and all rounds">
            <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden">
              <div className="overflow-x-auto">
              <table className="w-full text-xs min-w-[340px]">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-100">
                    <th className="text-left px-3 py-2.5 text-gray-500 font-bold w-7">#</th>
                    <th className="text-left px-3 py-2.5 text-gray-500 font-bold">Team</th>
                    <th className="text-center px-2 py-2.5 text-green-600 font-bold whitespace-nowrap">Wins</th>
                    <th className="text-center px-2 py-2.5 text-amber-600 font-bold whitespace-nowrap">Pts</th>
                    <th className="px-3 py-2.5 text-gray-400 font-bold min-w-[60px]">Share</th>
                  </tr>
                </thead>
                <tbody>
                  {overallPtsTable.map((t, i) => {
                    const sharePct = Math.round(t.pts / totalPts * 100);
                    return (
                      <tr key={t.name} className={`border-b border-gray-50 last:border-0 ${i===0?'bg-amber-50/30':''}`}>
                        <td className="px-3 py-2.5 text-center">
                          <span className="font-bold text-sm" style={{color:i===0?'#b45309':i===1?'#6b7280':i===2?'#92400e':'#9ca3af'}}>
                            {i===0?'🥇':i===1?'🥈':i===2?'🥉':i+1}
                          </span>
                        </td>
                        <td className="px-3 py-2.5">
                          <div className="flex items-center gap-2">
                            <div className="h-5 w-5 rounded-md flex-shrink-0 flex items-center justify-center font-bold text-[9px] text-white"
                              style={{ backgroundColor: t.color||'#3b82f6' }}>
                              {t.name?.[0]}
                            </div>
                            <span className="font-semibold text-gray-800 truncate max-w-[130px] sm:max-w-none">{t.name}</span>
                          </div>
                        </td>
                        <td className="px-2 py-2.5 text-center font-bold text-green-600">{t.wins}</td>
                        <td className="px-2 py-2.5 text-center font-black text-amber-600 text-sm">{t.pts}</td>
                        <td className="px-3 py-2.5">
                          <div className="flex items-center gap-1.5">
                            <div className="flex-1 bg-gray-100 rounded-full h-2 overflow-hidden">
                              <div className="h-full rounded-full bg-amber-400" style={{ width:`${sharePct}%` }}/>
                            </div>
                            <span className="text-[9px] text-gray-500 font-semibold w-7 text-right flex-shrink-0">{sharePct}%</span>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              </div>
              <div className="px-4 py-2 bg-gray-50 border-t border-gray-100 text-[10px] text-gray-400 text-center">
                2 pts per individual match win · All sports · All rounds · Share % = team's share of total {totalPts} points
              </div>
            </div>
          </Section>
        )}



        {/* ══ 4b. PLAYERS WITHOUT A WIN ════════════════════════════════ */}
        {zeroWinPlayers.length > 0 && (
          <Section title="Players Without a Win" icon="⚠️" newPage
            subtitle={`${zeroWinPlayers.length} players participated but have not won a match · shown by team`}>
            {(() => {
              const byTeam = {};
              zeroWinPlayers.forEach(p => {
                const info = playerTeamMap[p.player_name?.toLowerCase()];
                const tName = info?.teamName || 'Unassigned';
                if (!byTeam[tName]) byTeam[tName] = { count: 0, played: 0 };
                byTeam[tName].count  += 1;
                byTeam[tName].played += p.played;
              });
              return (
                <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
                  <div className="px-4 py-2.5 bg-gray-50 border-b border-gray-100 flex items-center justify-between">
                    <p className="text-xs font-bold text-gray-500 uppercase tracking-widest">Team Breakdown</p>
                    <p className="text-xs text-gray-400">{zeroWinPlayers.length} total · {zeroWinPlayers.reduce((s,p)=>s+p.played,0)} matches played</p>
                  </div>
                  <div className="divide-y divide-gray-50">
                    {Object.entries(byTeam).sort((a,b)=>b[1].count-a[1].count).map(([tName, d]) => (
                      <div key={tName} className="flex items-center gap-3 px-4 py-3 text-xs">
                        <span className="text-xs font-semibold text-gray-800 flex-1 truncate">{tName}</span>
                        <span className="text-red-600 font-black">{d.count} players</span>
                        <span className="text-gray-400">{d.played} matches played</span>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })()}
          </Section>
        )}


        {/* ══ 5. DOMINANT WINS ════════════════════════════════════════ */}
        {dominantMatches.length > 0 && (
          <Section title="Dominant Performances" icon="💪" subtitle="Biggest winning margins">
            <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden">
              <div className="divide-y divide-gray-100">
                {dominantMatches.slice(0,8).map((m,i) => (
                  <div key={i} className="flex items-center gap-3 px-4 py-3 text-xs">
                    <SportIcon sport={m.sc} size={20}/>
                    <div className="flex-1 min-w-0">
                      <p className="font-bold text-green-600 truncate">{m.winner}</p>
                      <p className="text-gray-500 truncate">vs {m.loser} · {m.slot||''}{m.rt&&m.rt!=='group'?` · ${m.rt.toUpperCase()}`:''}</p>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className="font-black text-green-600">{m.ws}–{m.ls}</p>
                      <p className="text-[10px] text-gray-500">+{m.diff} pts</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </Section>
        )}

        {/* ══ 6. NAIL-BITERS ══════════════════════════════════════════ */}
        {closeMatches.length > 0 && (
          <Section title="Nail-biters" icon="😅" subtitle="Matches decided by 2 points or fewer">
            <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden">
              <div className="divide-y divide-gray-100">
                {closeMatches.slice(0,8).map((m,i) => (
                  <div key={i} className="flex items-center gap-3 px-4 py-3 text-xs">
                    <SportIcon sport={m.sc} size={20}/>
                    <div className="flex-1 min-w-0">
                      <p className="font-bold text-amber-600 truncate">{m.winner}</p>
                      <p className="text-gray-500 truncate">edged {m.loser} · {m.slot||''}{m.rt&&m.rt!=='group'?` · ${m.rt.toUpperCase()}`:''}</p>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className="font-black text-amber-600">{m.ws}–{m.ls}</p>
                      <p className="text-[10px] text-gray-500">by {m.diff} pt{m.diff!==1?'s':''}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </Section>
        )}

        {/* ══ 7. AUCTION POINTS vs WIN ══════════════════════════════ */}
        {bidPerformance.length > 0 && (
          <Section title="Auction Bid vs Match Wins" icon="💰" newPage
            subtitle="Top 20 players by bid amount · Bid ÷ Wins = cost per win · lowest ₹/Win = best value">

            {/* Chart: horizontal bars, sorted by bid amount descending */}
            {(() => {
              const top20 = [...bidPerformance]
                .sort((a, b) => a.bid - b.bid)  // lowest bid first (ascending)
                .slice(0, 15);
              const maxBarBid  = Math.max(...top20.map(p => p.bid),  1);
              const maxBarWins = Math.max(...top20.map(p => p.wins), 1);
              // Find best value (min cost per win, wins > 0)
              const bestPick = top20.filter(p => p.costPerWin).sort((a,b) => a.costPerWin - b.costPerWin)[0];

              return (
                <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
                  {/* Chart header */}
                  <div className="px-4 py-2.5 bg-gray-50 border-b border-gray-100 flex items-center gap-6 text-[10px] text-gray-500">
                    <span className="font-bold uppercase tracking-widest flex-1">Player</span>
                    <span className="flex items-center gap-1"><span className="inline-block w-3 h-2 bg-blue-400 rounded-sm"/>Bid</span>
                    <span className="flex items-center gap-1"><span className="inline-block w-3 h-2 bg-green-500 rounded-sm"/>Wins</span>
                    <span className="font-bold text-purple-600">₹/Win</span>
                  </div>

                  <div className="divide-y divide-gray-50">
                    {top20.map((p, i) => {
                      const bidBarPct = Math.round(p.bid  / maxBarBid  * 100);
                      const winBarPct = Math.round(p.wins / maxBarWins * 100);
                      const isBest    = bestPick && p.player_id === bestPick.player_id;
                      const cpw = p.costPerWin;
                      return (
                        <div key={p.player_id}
                          className={`px-4 py-2.5 ${isBest ? 'bg-green-50' : p.wins === 0 ? 'bg-red-50/30' : ''}`}>
                          {/* Row header */}
                          <div className="flex items-center gap-2 mb-1.5">
                            <span className="text-[10px] text-gray-400 w-5 text-right flex-shrink-0">{i+1}</span>
                            <p className="text-xs font-bold text-gray-800 flex-1 truncate">
                              {p.player_name}
                              {isBest && <span className="ml-1.5 text-[9px] text-green-600 font-black">🔥 BEST VALUE</span>}
                            </p>
                            <span className="text-[10px] font-mono text-blue-600 flex-shrink-0">
                              ₹{p.bid >= 1000 ? `${(p.bid/1000).toFixed(1)}K` : p.bid}
                            </span>
                            <span className="text-[10px] font-black flex-shrink-0 w-6 text-center"
                              style={{color: p.wins > 0 ? '#16a34a' : '#dc2626'}}>
                              {p.wins}W
                            </span>
                            <span className="text-[10px] font-black text-purple-700 flex-shrink-0 w-16 text-right">
                              {cpw ? `₹${cpw >= 1000 ? `${(cpw/1000).toFixed(1)}K` : cpw}/W` : '—'}
                            </span>
                          </div>
                          {/* Dual horizontal bars */}
                          <div className="space-y-1 ml-5">
                            <div className="flex items-center gap-2">
                              <span className="text-[8px] text-blue-500 w-5 flex-shrink-0">Bid</span>
                              <div className="flex-1 bg-gray-100 rounded-full overflow-hidden h-2">
                                <div className="h-full rounded-full bg-blue-400 transition-all" style={{ width:`${bidBarPct}%` }}/>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="text-[8px] text-green-600 w-5 flex-shrink-0">Wins</span>
                              <div className="flex-1 bg-gray-100 rounded-full overflow-hidden h-2">
                                <div className="h-full rounded-full bg-green-500 transition-all" style={{ width:`${winBarPct}%` }}/>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  <div className="px-4 py-2.5 bg-gray-50 border-t border-gray-100 text-[10px] text-gray-400 text-center">
                    Sorted by bid amount (highest first) · ₹/Win = Bid ÷ Match Wins · Lower ₹/Win = better value
                  </div>
                </div>
              );
            })()}
          </Section>
        )}

        <p className="text-center text-[10px] text-gray-500/40 pb-8">
          BTCCP · Circles Sports League · Generated {new Date().toLocaleDateString('en-IN')}
        </p>
      </div>
    </div>
  );
}
